import { useState } from "react";
import { baseUrl, translate } from "@mediaspace/shared/utils";
import {
    Button,
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    Typography,
} from "@kaltura/ds-react-components";
import styled from "@emotion/styled";
import ReactHtmlParser from "@mediaspace/shared/ui/html-react-parser";
import { RegistrationType } from "./SiteRegistrationProps";

export interface Props {
    registrationType: RegistrationType;
    errorMessage?: string;
}

const GreyText = styled(Typography)(({ theme }) => ({
    /* Tone (Light Theme)/Tone 2 */
    color: theme.kaltura.palette.tone2,
    display: "block",
}));

const StyledActions = styled(ModalActions)(({ theme }) => ({
    columnGap: theme.spacing(1),
}));

const CenteredModalContent = styled(ModalContent)({
    textAlign: "center",
});

/**
 * Site registration errors
 */
export const RegistrationErrors = ({
    registrationType = RegistrationType.NEW,
    errorMessage,
}: Props) => {
    const [showModal, setShowModal] = useState(true);

    // hide modal
    const handleHideModal = () => {
        setShowModal(false);
    };

    return (
        <Modal
            open={showModal}
            onClose={handleHideModal}
            classes={{
                paper: "kms-ds-site-registration-error-paper",
                root: "kms-ds-site-registration-error",
            }}
        >
            <ModalTitle>{translate("Something went wrong")}</ModalTitle>
            <CenteredModalContent>
                {errorMessage && <GreyText>{ReactHtmlParser(errorMessage)}</GreyText>}
                {!errorMessage && registrationType === RegistrationType.NEW && (
                    <GreyText variant={"body2"}>
                        {translate(
                            "Something went wrong during the sign-up process."
                        )}
                        <br/>
                        {translate(
                            "If you previously completed the registration form, and need to verify your email address, please click here."
                        )}
                    </GreyText>
                )}
                {!errorMessage && registrationType === RegistrationType.EDIT && (
                    <GreyText variant={"body2"}>
                        {translate(
                            "Something went wrong during the update process."
                        )}
                        <br/>
                        {translate(
                            "Please try again later or contact support."
                        )}
                    </GreyText>
                )}
                {!errorMessage && registrationType === RegistrationType.INVITE && (
                    <GreyText variant={"body2"}>
                        {translate(
                            "Something went wrong with your invitation."
                        )}
                        <br/>
                        {translate(
                            "To request a new invitation and restart the flow, request a new email by clicking below."
                        )}
                    </GreyText>
                )}
            </CenteredModalContent>
            <StyledActions>
                {!errorMessage && registrationType !== RegistrationType.EDIT && (
                    <>
                        <Button
                            variant={"borderless"}
                            onClick={handleHideModal}
                            key={"close"}
                        >
                            {translate("Close")}
                        </Button>

                        <Button href={`${baseUrl}/registration/activate`}>
                            {registrationType === RegistrationType.NEW
                                ? translate("Verify Email Address")
                                : translate("Resend invitation email")
                            }
                        </Button>
                    </>
                )}

                {(errorMessage || (!errorMessage && registrationType === RegistrationType.EDIT)) && (
                    <Button onClick={handleHideModal} key={"close"}>
                        {translate("Close")}
                    </Button>
                )}
            </StyledActions>
        </Modal>
    );
};

export default RegistrationErrors;
